import React from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Calendar, Users, BookOpen, Building2, HelpCircle, FileText, GraduationCap, Stethoscope, ExternalLink } from 'lucide-react';

const LandingPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section with 4 Primary CTAs */}
      <section className="bg-gradient-to-br from-slate-700 to-slate-900 text-white py-20">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Welcome to Health City
          </h1>
          <p className="text-xl md:text-2xl text-slate-200 mb-10">
            Professional Mental Health & Wellbeing Services
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            <Link 
              to="/book-now" 
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-4 rounded-lg font-semibold transition-all duration-200 flex flex-col items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:scale-105"
            >
              <Calendar className="w-8 h-8" />
              <span className="text-sm">Book Appointment</span>
            </Link>
            
            <Link 
              to="/teams" 
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-4 rounded-lg font-semibold transition-all duration-200 flex flex-col items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:scale-105"
            >
              <Users className="w-8 h-8" />
              <span className="text-sm">Browse Clinicians</span>
            </Link>
            
            <a 
              href="https://healthcity.org.uk/quiz" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-4 rounded-lg font-semibold transition-all duration-200 flex flex-col items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:scale-105"
            >
              <HelpCircle className="w-8 h-8" />
              <span className="text-sm">Get Matched (Quiz)</span>
              <ExternalLink className="w-4 h-4 absolute top-2 right-2" />
            </a>
            
            <Link 
              to="/contact" 
              className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-4 rounded-lg font-semibold transition-all duration-200 flex flex-col items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:scale-105"
            >
              <MessageSquare className="w-8 h-8" />
              <span className="text-sm">Send Enquiry</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        
        {/* What Are You Looking For? Section */}
        <section className="mb-20">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-10 text-center">
            What Are You Looking For?
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            
            {/* Client Services Card */}
            <div className="bg-white border-2 border-blue-200 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-200 hover:border-blue-400">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-blue-100 p-3 rounded-full">
                  <Stethoscope className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-800">I Am a Client</h3>
              </div>
              
              <p className="text-gray-600 mb-4 font-medium">Looking for:</p>
              
              <div className="space-y-3 mb-6">
                <Link 
                  to="/book-now" 
                  className="block w-full bg-blue-50 hover:bg-blue-100 border border-blue-300 text-blue-700 py-3 px-4 rounded-lg text-center font-medium transition-all duration-200 hover:shadow-md"
                >
                  Assessment & Diagnostics
                </Link>
                <Link 
                  to="/book-now" 
                  className="block w-full bg-blue-50 hover:bg-blue-100 border border-blue-300 text-blue-700 py-3 px-4 rounded-lg text-center font-medium transition-all duration-200 hover:shadow-md"
                >
                  Treatment & Therapy
                </Link>
                <Link 
                  to="/book-now" 
                  className="block w-full bg-blue-50 hover:bg-blue-100 border border-blue-300 text-blue-700 py-3 px-4 rounded-lg text-center font-medium transition-all duration-200 hover:shadow-md"
                >
                  Rehabilitation Services
                </Link>
                <Link 
                  to="/book-now" 
                  className="block w-full bg-blue-50 hover:bg-blue-100 border border-blue-300 text-blue-700 py-3 px-4 rounded-lg text-center font-medium transition-all duration-200 hover:shadow-md"
                >
                  Self-Help Training
                </Link>
              </div>
              
              <Link 
                to="/book-now" 
                className="block w-full bg-blue-600 hover:bg-blue-700 text-white py-4 px-6 rounded-lg text-center font-bold transition-colors duration-200 shadow-md hover:shadow-lg"
              >
                BOOK APPOINTMENT NOW
              </Link>
            </div>

            {/* Professional/Organization Services Card */}
            <div className="bg-white border-2 border-green-200 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-200 hover:border-green-400">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-green-100 p-3 rounded-full">
                  <Building2 className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-800">I Am a Professional or an Organisation</h3>
              </div>
              
              <p className="text-gray-600 mb-4 font-medium">I need:</p>
              
              <div className="space-y-2 mb-6">
                <Link 
                  to="/book-now" 
                  className="block w-full bg-green-50 hover:bg-green-100 border border-green-300 text-green-700 py-2.5 px-4 rounded-lg text-center font-medium transition-all duration-200 hover:shadow-md text-sm"
                >
                  Referral Services
                </Link>
                <Link 
                  to="/book-now" 
                  className="block w-full bg-green-50 hover:bg-green-100 border border-green-300 text-green-700 py-2.5 px-4 rounded-lg text-center font-medium transition-all duration-200 hover:shadow-md text-sm"
                >
                  Supervision
                </Link>
                <Link 
                  to="/coaching-staff-wellbeing" 
                  className="block w-full bg-green-50 hover:bg-green-100 border border-green-300 text-green-700 py-2.5 px-4 rounded-lg text-center font-medium transition-all duration-200 hover:shadow-md text-sm"
                >
                  Staff Wellbeing Services
                </Link>
                <Link 
                  to="/book-now" 
                  className="block w-full bg-green-50 hover:bg-green-100 border border-green-300 text-green-700 py-2.5 px-4 rounded-lg text-center font-medium transition-all duration-200 hover:shadow-md text-sm"
                >
                  Mentoring
                </Link>
                <Link 
                  to="/book-now" 
                  className="block w-full bg-green-50 hover:bg-green-100 border border-green-300 text-green-700 py-2.5 px-4 rounded-lg text-center font-medium transition-all duration-200 hover:shadow-md text-sm"
                >
                  Training
                </Link>
                <Link 
                  to="/book-now" 
                  className="block w-full bg-green-50 hover:bg-green-100 border border-green-300 text-green-700 py-2.5 px-4 rounded-lg text-center font-medium transition-all duration-200 hover:shadow-md text-sm"
                >
                  Consultation
                </Link>
                <Link 
                  to="/contact" 
                  className="block w-full bg-green-50 hover:bg-green-100 border border-green-300 text-green-700 py-2.5 px-4 rounded-lg text-center font-medium transition-all duration-200 hover:shadow-md text-sm"
                >
                  Partnership Enquiry
                </Link>
                <Link 
                  to="/contact" 
                  className="block w-full bg-green-50 hover:bg-green-100 border border-green-300 text-green-700 py-2.5 px-4 rounded-lg text-center font-medium transition-all duration-200 hover:shadow-md text-sm"
                >
                  Join as Associate
                </Link>
                <Link 
                  to="/contact" 
                  className="block w-full bg-green-50 hover:bg-green-100 border border-green-300 text-green-700 py-2.5 px-4 rounded-lg text-center font-medium transition-all duration-200 hover:shadow-md text-sm"
                >
                  Volunteering / Career Enquiry
                </Link>
              </div>
            </div>
          </div>
          
          {/* Send Enquiry Message Section */}
          <div className="mt-8 bg-gradient-to-r from-orange-50 to-orange-100 border-2 border-orange-300 rounded-xl p-6 text-center">
            <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center justify-center gap-2">
              <MessageSquare className="w-6 h-6 text-orange-600" />
              SEND ENQUIRY MESSAGE
            </h3>
            <Link 
              to="/contact" 
              className="inline-block bg-orange-600 hover:bg-orange-700 text-white py-3 px-8 rounded-lg font-semibold transition-colors duration-200"
            >
              Contact Us
            </Link>
          </div>
        </section>

        {/* Booking Path Options Section */}
        <section className="mb-20">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4 text-center">
            Booking Path Options
          </h2>
          <p className="text-gray-600 text-center mb-10 text-lg">
            Choose the workflow that works best for you
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            
            {/* Book Without Browsing Clinicians */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-200 hover:border-blue-300">
              <div className="text-center mb-4">
                <div className="bg-blue-100 p-4 rounded-full inline-block mb-4">
                  <Calendar className="w-10 h-10 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">Book Without Browsing Clinicians</h3>
              </div>
              <p className="text-gray-700 mb-6 text-sm leading-relaxed">
                Auto-match clinician if you already know the service type. We'll allocate you to one of our qualified professionals for consultation or assessment.
              </p>
              <Link 
                to="/book-now" 
                className="block w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg text-center font-medium transition-colors duration-200"
              >
                Book Now & Payment Form
              </Link>
            </div>

            {/* Browse or Search Clinicians */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-200 hover:border-green-300">
              <div className="text-center mb-4">
                <div className="bg-green-100 p-4 rounded-full inline-block mb-4">
                  <Users className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">Browse or Search Clinicians</h3>
              </div>
              <p className="text-gray-700 mb-6 text-sm leading-relaxed">
                View our clinicians' profiles and CVs. Each clinician card links to the appropriate service type on the Book Now page.
              </p>
              <Link 
                to="/teams" 
                className="block w-full bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg text-center font-medium transition-colors duration-200"
              >
                View Clinicians
              </Link>
            </div>

            {/* Get Matched Assistance */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-200 hover:border-purple-300">
              <div className="text-center mb-4">
                <div className="bg-purple-100 p-4 rounded-full inline-block mb-4">
                  <HelpCircle className="w-10 h-10 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">Get Matched Assistance</h3>
              </div>
              <p className="text-gray-700 mb-6 text-sm leading-relaxed">
                Not sure which clinician or service is best for you? Complete our quiz and we'll match you to the right professional.
              </p>
              <a 
                href="https://healthcity.org.uk/quiz" 
                target="_blank" 
                rel="noopener noreferrer"
                className="relative block w-full bg-purple-600 hover:bg-purple-700 text-white py-3 px-4 rounded-lg text-center font-medium transition-colors duration-200"
              >
                Take the Quiz
                <ExternalLink className="w-4 h-4 absolute top-3 right-3" />
              </a>
            </div>
          </div>
        </section>

        {/* Additional CTA Links Section */}
        <section className="mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4 text-center">
            Additional Resources
          </h2>
          <p className="text-gray-600 text-center mb-10 text-lg">
            Explore our knowledge base and learning materials
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            
            {/* READ BLOG */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-200 hover:border-orange-300">
              <div className="text-center mb-4">
                <div className="bg-orange-100 p-4 rounded-full inline-block mb-4">
                  <FileText className="w-10 h-10 text-orange-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">READ BLOG</h3>
              </div>
              <p className="text-gray-700 mb-6 text-sm text-center">
                Stay updated with mental health insights, tips, and the latest from our team.
              </p>
              <Link 
                to="/blog" 
                className="block w-full bg-orange-600 hover:bg-orange-700 text-white py-3 px-4 rounded-lg text-center font-medium transition-colors duration-200"
              >
                Visit Blog
              </Link>
            </div>

            {/* READ A BOOK */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-200 hover:border-red-300">
              <div className="text-center mb-4">
                <div className="bg-red-100 p-4 rounded-full inline-block mb-4">
                  <BookOpen className="w-10 h-10 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">READ A BOOK</h3>
              </div>
              <p className="text-gray-700 mb-6 text-sm text-center">
                Explore our published books on mental health and wellbeing topics on Lulu Store.
              </p>
              <a 
                href="https://www.lulu.com/spotlight/HealthCity" 
                target="_blank" 
                rel="noopener noreferrer"
                className="relative block w-full bg-red-600 hover:bg-red-700 text-white py-3 px-4 rounded-lg text-center font-medium transition-colors duration-200"
              >
                Browse Books on Lulu
                <ExternalLink className="w-4 h-4 absolute top-3 right-3" />
              </a>
            </div>

            {/* WANT TO KNOW MORE */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-200 hover:border-teal-300">
              <div className="text-center mb-4">
                <div className="bg-teal-100 p-4 rounded-full inline-block mb-4">
                  <GraduationCap className="w-10 h-10 text-teal-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">WANT TO KNOW MORE</h3>
                <p className="text-sm text-gray-500 mb-4">About Clinicians, Assessments & Treatments</p>
              </div>
              <div className="space-y-2">
                <Link 
                  to="/teams" 
                  className="block w-full bg-teal-600 hover:bg-teal-700 text-white py-2 px-4 rounded-lg text-center font-medium transition-colors duration-200 text-sm"
                >
                  Teams Menu
                </Link>
                <Link 
                  to="/assessments/free" 
                  className="block w-full bg-teal-600 hover:bg-teal-700 text-white py-2 px-4 rounded-lg text-center font-medium transition-colors duration-200 text-sm"
                >
                  Assessments/Diagnostics
                </Link>
                <Link 
                  to="/treatments" 
                  className="block w-full bg-teal-600 hover:bg-teal-700 text-white py-2 px-4 rounded-lg text-center font-medium transition-colors duration-200 text-sm"
                >
                  Treatments
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default LandingPage;